// Shape of get_super_admin_dashboard() (migrations 20260928231500 + 20260929001500) plus the pure
// helpers the super admin home uses to turn it into labels and alerts.
// Web only — super admins don't use the mobile app.

export interface BackupJob {
  job: string;
  label: string;
  last_status: 'success' | 'error' | 'running' | 'never_ran' | string;
  last_started_at: string | null;
  last_error: string | null;
  last_success_at: string | null;
  size_bytes: number | null;
  max_age_hours: number;
  is_stale: boolean;
  recent: { at: string; status: string }[];
}

export interface SerpRow {
  position: number | null;
  position_prev: number | null;
}

export interface ServerStats {
  // Disk fields are absent until the VPS reporter (server-stats.sh) has run once.
  reported_at?: string | null;
  disk_used_bytes?: number | null;
  disk_free_bytes?: number | null;
  is_stale?: boolean;
  db_size_bytes: number;
}

export interface SuperAdminDashboardData {
  generated_at: string;
  backups: BackupJob[];
  server?: ServerStats | null;
  signups: {
    users_7d: number; users_prev_7d: number; users_total: number;
    agencies_7d: number; agencies_prev_7d: number;
  } | null;
  clients: {
    total: number; active: number; paying: number; suspended: number; trial: number;
    mrr_usd?: number | null; mrr_usd_prev?: number | null; snapshot_date?: string | null;
  } | null;
  leads: { portal_7d: number; portal_prev_7d: number; portal_total: number; since: string | null } | null;
  // NULL when the analytics tables don't exist (local / fresh DB).
  traffic: {
    page_views_7d: number; page_views_prev_7d: number;
    sessions_7d: number; sessions_prev_7d: number;
    property_views_7d: number; property_views_prev_7d: number;
    from: string; through: string; has_data: boolean;
  } | null;
  serp: (SerpRow & {
    clicks_7d: number; clicks_prev_7d: number; impressions_7d: number;
    from: string; through: string; has_data: boolean;
    portals: (SerpRow & { site: string; clicks_7d: number; clicks_prev_7d: number })[];
    keywords: (SerpRow & { query: string; impressions: number })[];
  }) | null;
  crons: {
    total: number; stale: number;
    problems: { job: string; last_status: string | null; last_success_at: string | null; last_error: string | null }[];
  } | null;
}

/** Percent change vs the previous window, or null when there is no baseline to compare against. */
export function pctChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/**
 * Places gained in Google vs the previous window. Positive = improved, because
 * a LOWER position number ranks higher (position 3 beats position 5).
 */
export function positionGain(row: SerpRow): number | null {
  if (row.position == null || row.position_prev == null) return null;
  return Math.round((row.position_prev - row.position) * 10) / 10;
}

export type BackupState = 'ok' | 'running' | 'failed' | 'stale' | 'never';

// A run still "running" after this long died without reporting back.
const RUNNING_GRACE_MS = 3 * 3600_000;

export function backupState(b: BackupJob, now: number = Date.now()): BackupState {
  if (b.last_status === 'never_ran') return 'never';
  if (b.last_status === 'error') return 'failed';
  if (b.last_status === 'running' && b.last_started_at
      && now - new Date(b.last_started_at).getTime() < RUNNING_GRACE_MS) {
    // A fresh run in progress only counts as healthy while the last good copy is recent.
    return b.is_stale ? 'stale' : 'running';
  }
  return b.is_stale ? 'stale' : 'ok';
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || bytes < 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 ** 3) return `${Math.round(bytes / 1024 ** 2)} MB`;
  // One decimal only while it changes the reading ("4.7 GB" yes, "471.8 GB" no).
  const gb = bytes / 1024 ** 3;
  return `${gb < 10 ? gb.toFixed(1) : Math.round(gb)} GB`;
}

export type DiskLevel = 'ok' | 'warning' | 'danger';

// Share of the disk in use at which the dashboard starts warning. A full disk
// stops Postgres, so the red line leaves room to react.
export const DISK_WARNING_PCT = 80;
export const DISK_DANGER_PCT = 90;

/**
 * Disk usage as df reports it (used / (used + free)), or null when the server
 * has not reported or its last report is stale.
 */
export function diskUsage(server: ServerStats | null | undefined):
  { usedPct: number; freeBytes: number; totalBytes: number; level: DiskLevel } | null {
  if (!server || server.is_stale) return null;
  const used = server.disk_used_bytes, free = server.disk_free_bytes;
  if (used == null || free == null || used + free <= 0) return null;
  const usedPct = Math.round((used / (used + free)) * 100);
  const level: DiskLevel = usedPct >= DISK_DANGER_PCT ? 'danger' : usedPct >= DISK_WARNING_PCT ? 'warning' : 'ok';
  return { usedPct, freeBytes: free, totalBytes: used + free, level };
}

export interface DashboardAlert {
  tone: 'danger' | 'warning';
  text: string;
}

/** Everything on the dashboard that needs a human, worst first. Empty = all good. */
export function dashboardAlerts(d: SuperAdminDashboardData, now: number = Date.now()): DashboardAlert[] {
  const alerts: DashboardAlert[] = [];
  for (const b of d.backups) {
    const state = backupState(b, now);
    if (state === 'failed') {
      alerts.push({ tone: 'danger', text: `El respaldo de ${b.label} falló${b.last_error ? ` (${b.last_error})` : ''}.` });
    } else if (state === 'stale') {
      alerts.push({ tone: 'danger', text: `${b.label}: sin respaldo exitoso en las últimas ${b.max_age_hours} horas.` });
    } else if (state === 'never') {
      alerts.push({ tone: 'warning', text: `${b.label}: todavía no hay ningún respaldo registrado.` });
    }
  }
  const disk = diskUsage(d.server);
  if (disk && disk.level !== 'ok') {
    alerts.push({
      tone: disk.level,
      text: `El disco del servidor está al ${disk.usedPct}%: quedan ${formatBytes(disk.freeBytes)} libres.`,
    });
  } else if (d.server?.is_stale) {
    alerts.push({ tone: 'warning', text: 'El servidor no reporta su almacenamiento desde hace más de 3 horas.' });
  }
  if (d.crons && d.crons.stale > 0) {
    const names = d.crons.problems.map(p => p.job).join(', ');
    alerts.push({
      tone: 'warning',
      text: `${d.crons.stale} ${d.crons.stale === 1 ? 'tarea programada atrasada' : 'tareas programadas atrasadas'}: ${names}.`,
    });
  }
  return alerts.sort((a, b) => Number(b.tone === 'danger') - Number(a.tone === 'danger'));
}
